import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { ActionMenuItem } from './ActionMenuItem.tsx';
import type { LegacyProjectFieldsPageMountEfaOptInMenuItemQuery } from './__generated__/LegacyProjectFieldsPageMountEfaOptInMenuItemQuery.graphql';

/**
 * We only intend to render the contents of this component when
 * the feature gate 'mc-nfa-soft-rollback' is disabled.
 *
 * This is a temporary workaround so we can roll out the first phase of EFA for CMP.
 *
 * The second phase will be to replace the page at the 'legacy-project-settings-fields'
 * URL with a new page served by AFM SPA. Once rolled out, this code can then be removed.
 */
export const LegacyProjectFieldsPageMountEfaOptInMenuItem = (): React.ReactPortal | null => {
	const [portal, setPortal] = useState<React.ReactPortal | null>(null);

	const cloudId = useCloudId();
	const graphqlData = useLazyLoadQuery<LegacyProjectFieldsPageMountEfaOptInMenuItemQuery>(
		graphql`
			query LegacyProjectFieldsPageMountEfaOptInMenuItemQuery(
				$cloudId: ID!
				$isJiraEfaForCmpM2Experiment: Boolean!
			) {
				jira_isTenantConvertedToNfa(cloudId: $cloudId)
					@optIn(to: "JiraIsTenantConvertedToNfa")
					@include(if: $isJiraEfaForCmpM2Experiment)
			}
		`,
		{
			cloudId,
			isJiraEfaForCmpM2Experiment: !fg('mc-nfa-soft-rollback'),
		},
	);

	// Remove possibility for errors due to graphqlData or jira_isTenantConvertedToNfa being null.
	const isTenantConvertedToNfa: boolean = graphqlData?.jira_isTenantConvertedToNfa ?? false;
	// Restrict visibility of new menu item
	const showNewOptInModal = useMemo(() => {
		return !fg('mc-nfa-soft-rollback') && isTenantConvertedToNfa;
	}, [isTenantConvertedToNfa]);

	// Apply useEffect() to avoid SSR + querying page for every render
	useEffect(() => {
		// Changes to legacy content in rootElement are permanent, so we do nothing
		// unless showNewOptInModal is confirmed and can't change back to false.
		if (!showNewOptInModal) {
			return;
		}
		if (!globalThis?.document?.querySelector) {
			return;
		}

		const linkElement = globalThis.document.querySelector<HTMLElement>(
			'#project-config-fields-scheme-change',
		);
		const projectId = linkElement?.dataset.id;
		const rootElement = linkElement?.parentElement; // from <a> to <li> in menu

		if (!rootElement || !projectId) {
			// eslint-disable-next-line no-console
			console.warn(
				'LegacyProjectFieldsPageMountEfaOptInMenuItem: No root element or project ID found.',
			);
			return;
		}

		// Empty existing contents from legacy page template
		rootElement.replaceChildren('');

		// Restore focus element
		const focusElement =
			globalThis.document.querySelector<HTMLElement>('#project-config-tab-actions') ?? undefined;

		setPortal(
			createPortal(
				<ActionMenuItem projectId={projectId} restoreFocusElement={focusElement} />,
				rootElement,
				projectId,
			),
		);
	}, [showNewOptInModal]);

	// Hook React into the mount point inside the menu.
	// This will replace the legacy menu <li> item (a link to another page)
	// with our own React rendered component which opens a modal.
	return portal;
};
